import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { formatDate } from '@/util/formatting';
import SyncedCheckmark from '../../edit/SyncedCheckmark';

type AssetImageDetailsProps = {
  /** null = not stamped yet (the DV-2855 backfill ships off per environment). */
  imageID?: number | null;
  createdAt?: string;
};

type CopyResult = 'ok' | 'failed';

/**
 * Long enough for the checkmark to finish drawing (~1.0s: pop 0.6s, circle
 * 0.6s at +0.15s, tick 0.4s at +0.6s) and then rest before it retracts.
 */
const HOLD_MS = 1600;
/** Matches the opacity transition below, so the node unmounts after the fade. */
const FADE_MS = 240;

/**
 * DV-2856 "Show Image Details" row: the photo's Image ID and upload date,
 * rendered under the thumbnail inside the card's ring.
 *
 * Three agreed corrections to the design prototype are load-bearing here:
 * bare digits (no "IMG-" prefix), ID + date only (no source label), and no
 * internal-only lock — a Customer Admin sees exactly what an editor sees.
 *
 * Stacks below `sm` and sits on one line at `sm`+, per the AC: the ID wraps on
 * narrow screens rather than truncating, so a support ticket's number is never
 * half-visible.
 *
 * The ID is set in mono with tabular figures. It is a permanent serial, not
 * prose — proportional digits wobble down a grid of cards, and this is already
 * the house treatment for an identifier (see SlugCopy's customerEntityId).
 */
const AssetImageDetails = ({ imageID, createdAt }: AssetImageDetailsProps) => {
  const id = imageID == null ? null : String(imageID);
  // TWO pieces of state, not one, because the confirmation leaves in two
  // phases. `result` controls mounting; `showing` controls opacity. See the
  // comment on the confirmation element below for why that split is required.
  const [result, setResult] = useState<CopyResult | null>(null);
  const [showing, setShowing] = useState(false);
  const idRef = useRef<HTMLButtonElement>(null);
  const fadeTimer = useRef<ReturnType<typeof setTimeout>>();
  const unmountTimer = useRef<ReturnType<typeof setTimeout>>();

  // A copy confirmation outlives its card easily (the grid re-renders on
  // filter, sync, reorder), so both pending phases have to be cancellable.
  useEffect(
    () => () => {
      clearTimeout(fadeTimer.current);
      clearTimeout(unmountTimer.current);
    },
    []
  );

  /**
   * Last resort when the clipboard is refused: put the ID in the user's
   * selection so ⌘C works immediately, turning a dead end into a recovery.
   * Entirely cosmetic, and Selection is patchy across environments — a throw
   * here must not escape the copy handler.
   */
  const selectId = () => {
    const node = idRef.current;
    if (!node) return;
    try {
      const range = document.createRange();
      range.selectNodeContents(node);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    } catch {
      // No Selection support: the ID is still visible and selectable by hand.
    }
  };

  const confirm = (outcome: CopyResult) => {
    clearTimeout(fadeTimer.current);
    clearTimeout(unmountTimer.current);
    setResult(outcome);
    setShowing(true);
    fadeTimer.current = setTimeout(() => setShowing(false), HOLD_MS);
    unmountTimer.current = setTimeout(
      () => setResult(null),
      HOLD_MS + FADE_MS
    );
  };

  const copyId = async () => {
    if (!id) return;
    try {
      await navigator.clipboard?.writeText(id);
      confirm('ok');
    } catch {
      // Clipboard denied (permission, insecure context, older browser). The ID
      // is rendered text on the card either way, so nothing is lost.
      confirm('failed');
      selectId();
    }
  };

  return (
    <div className="flex flex-col gap-0.5 border-t border-border bg-muted/35 px-2.5 py-1.5 text-xs sm:flex-row sm:items-center sm:gap-2">
      {id ? (
        <button
          ref={idRef}
          type="button"
          onClick={copyId}
          aria-label={`Copy Image ID ${id}`}
          title="Copy Image ID"
          // max-lg:min-h-11 carries the 44px touch floor at the call site, the
          // convention the "Show Hidden" link in AssetsImageGrid established
          // for small inline controls.
          className="inline-flex w-fit items-center rounded font-mono font-semibold tabular-nums text-foreground hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary max-lg:min-h-11"
        >
          {id}
        </button>
      ) : (
        <span className="font-mono font-semibold tabular-nums text-muted-foreground">
          —
        </span>
      )}
      <span aria-hidden className="hidden text-border sm:inline">
        ·
      </span>
      {/* The date and the copy confirmation share ONE grid cell, so the slot is
          as wide as the wider of the two and the row never reflows mid-fade.
          The date is faded, never unmounted — which is also what holds the
          width open while the confirmation is up. */}
      <span className="grid justify-items-start">
        <span
          className={cn(
            'col-start-1 row-start-1 text-muted-foreground transition-opacity duration-200 motion-reduce:transition-none',
            showing && 'opacity-0'
          )}
        >
          {createdAt ? formatDate(createdAt) : '—'}
        </span>
        {/* Mounted only while confirming, and unmounted a beat AFTER it fades
            (the `showing`/`result` split above). SyncedCheckmark's draw-in is a
            CSS `animation: … forwards`, which fires on mount and never again —
            keeping this node permanently mounted would replay nothing on the
            second copy and show an already-drawn, static mark. Unmounting it
            the instant the hold ends would fix the replay but pop it off screen
            with no fade, so the exit runs in two phases. */}
        {result && (
          <span
            aria-hidden
            className={cn(
              'col-start-1 row-start-1 inline-flex items-center gap-1 font-medium transition-opacity duration-200 motion-reduce:transition-none',
              !showing && 'opacity-0',
              result === 'failed' ? 'text-destructive' : 'text-success'
            )}
          >
            {/* Same glyph and animation as SyncStatusChip's synced state — the
                app's established "this worked" mark. Not on the failure path:
                a checkmark there would contradict the word next to it. */}
            {result === 'ok' && <SyncedCheckmark className="h-3.5 w-3.5" />}
            {result === 'failed' ? 'Copy Failed' : 'Copied'}
          </span>
        )}
      </span>
      {/* Announced separately from the crossfade: the visual date label stays
          mounted for the fade, so a live region wrapped around this slot would
          fight the two-phase mount rather than simply reporting the outcome. */}
      <span className="sr-only" role="status" aria-live="polite">
        {result === 'ok' ? `Copied Image ID ${id}` : ''}
        {result === 'failed' ? 'Copy failed — the Image ID is selected' : ''}
      </span>
    </div>
  );
};

export default AssetImageDetails;
